'use client';

import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import Link from 'next/link';
import Image from 'next/image';
import { FaBars, MdOutlineKeyboardArrowDown } from '@/components/icons';

// /clearance is a "Coming Soon" placeholder - linking it from every page
// sends shoppers to an empty page. Set back to true once it has real offers
// (and remove its noindex in app/clearance/page.tsx + re-add it to app/sitemap.ts).
const SHOW_CLEARANCE_LINK = false;

interface HeaderProps {
  handleClickIndex?: (index: number) => void;
  isVehicle?: boolean;
  make?: {
    title: string;
  };
  models?: Array<{
    title: string;
    slug: string;
  }>;
}

// Static menu data - moved outside component to prevent recreation on every render
type ShopLeafLink = { label: string; path: string };
type ShopGroupLink = { label: string; id: string; children: ShopLeafLink[] };
type ShopLink = ShopLeafLink | ShopGroupLink;

const SHOP_LINKS: ShopLink[] = [
  { label: 'Shop by Vehicle', path: '/shop-by-vehicle' },
  { label: 'Harley Filters', path: '/harley-filter' },
  {
    label: 'Universal Filters',
    id: 'universal',
    children: [
      { label: 'Cone / Cylinder', path: '/universal-cylinder' },
      { label: 'Universal Round', path: '/universal-round' },
    ],
  },
  { label: 'Panel Filters', path: '/universal-panel' },
  { label: 'Air Cleaner Assemblies', path: '/air-cleaner' },
  { label: 'Pedal Power Kit', path: '/power-pedal-kit' },
  {
    label: 'Cross Reference',
    id: 'crossReference',
    children: [
      { label: 'Other Brand → Green Filter', path: '/cross-reference-brand' },
      { label: 'Green Filter → Other Brand', path: '/cross-reference-filters' },
    ],
  },
];

const WHY_LINKS = [
  { label: 'Why We Outperform', path: '/why-go-green' },
  { label: 'About Us', path: '/about-us' },
  { label: 'Our Guarantee', path: '/our-guarantee' },
] as const;

const RESOURCES_LINKS = [
  { label: 'How to Measure a Filter', path: '/measure-filter' },
  { label: 'Blog', path: '/blog' },
  { label: 'FAQs', path: '/faqs' },
  { label: 'Private Label', path: '/private-label' },
] as const;

const Header: React.FC<HeaderProps> = ({ handleClickIndex = () => {}, isVehicle = false, make, models = [] }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [openShopGroupDesktop, setOpenShopGroupDesktop] = useState<string | null>(null);
  const [openShopGroupMobile, setOpenShopGroupMobile] = useState<string | null>(null);

  const shopDropdownRef = useRef<HTMLDivElement>(null);
  const whyDropdownRef = useRef<HTMLDivElement>(null);
  const resourcesDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const dropdownRefs = {
      shop: shopDropdownRef,
      why: whyDropdownRef,
      resources: resourcesDropdownRef,
    } as const;

    const handleClickOutside = (event: MouseEvent) => {
      if (!openDropdown) return;
      const activeRef = dropdownRefs[openDropdown as keyof typeof dropdownRefs];
      if (activeRef?.current && !activeRef.current.contains(event.target as Node)) {
        setOpenDropdown(null);
        setOpenShopGroupDesktop(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown]);

  const toggleAccordion = (key: string) => {
    const next = openAccordion === key ? null : key;
    setOpenAccordion(next);
    if (next !== 'shop') setOpenShopGroupMobile(null);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
    setOpenAccordion(null);
    setOpenShopGroupMobile(null);
  };

  const openDesktopDropdown = (name: string) => {
    setOpenDropdown(name);
    if (name !== 'shop') setOpenShopGroupDesktop(null);
  };

  const closeDesktopDropdown = (name: string) => {
    setOpenDropdown((prev) => (prev === name ? null : prev));
    if (name === 'shop') setOpenShopGroupDesktop(null);
  };

  const openShopGroupDesktopHandler = (id: string) => {
    setOpenShopGroupDesktop(id);
  };

  const closeShopGroupDesktopHandler = (id: string) => {
    setOpenShopGroupDesktop((prev) => (prev === id ? null : prev));
  };

  const toggleShopGroupMobile = (id: string) => {
    setOpenShopGroupMobile((prev) => (prev === id ? null : id));
  };

  return (
    <HeaderWrapper>
      {/* Mobile Drawer */}
      <MobileDrawer $isOpen={mobileMenuOpen}>
        <DrawerBackdrop onClick={handleMobileMenuClose} $isOpen={mobileMenuOpen} />
        <DrawerPanel $isOpen={mobileMenuOpen}>
          <DrawerContent>
            <div className="drawer-header">
              <button
                className="drawer-close"
                onClick={handleMobileMenuClose}
                aria-label="Close menu"
              >
                ×
              </button>
              <Link href="/" className="drawer-logo" onClick={handleMobileMenuClose}>
                <Image
                  src="/assets/gf-logo-registered.webp"
                  alt="Green Filter® logo"
                  width={162}
                  height={30}
                  priority
                />
              </Link>
            </div>

            {/* SHOP accordion */}
            <div className="accordion-row">
              <button
                className="accordion-trigger"
                onClick={() => toggleAccordion('shop')}
              >
                <span>SHOP</span>
                <span className="accordion-icon">
                  {openAccordion === 'shop' ? '×' : '+'}
                </span>
              </button>
              {openAccordion === 'shop' && (
                <div className="accordion-body">
                  {SHOP_LINKS.map((item) =>
                    'children' in item ? (
                      <div key={item.label} className="sub-accordion-row">
                        <button
                          type="button"
                          className="sub-accordion-trigger"
                          onClick={() => toggleShopGroupMobile(item.id)}
                        >
                          <span>{item.label}</span>
                          <span className="accordion-icon">
                            {openShopGroupMobile === item.id ? '×' : '+'}
                          </span>
                        </button>
                        {openShopGroupMobile === item.id && (
                          <div className="sub-accordion-body">
                            {item.children.map((child) => (
                              <Link
                                key={child.label}
                                href={child.path}
                                className="sub-link nested"
                                onClick={handleMobileMenuClose}
                              >
                                {child.label}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <Link
                        key={item.label}
                        href={item.path}
                        className="sub-link"
                        onClick={handleMobileMenuClose}
                      >
                        {item.label}
                      </Link>
                    )
                  )}
                </div>
              )}
            </div>

            {/* CLEARANCE */}
            {SHOW_CLEARANCE_LINK && (
              <div className="accordion-row">
                <Link href="/clearance" onClick={handleMobileMenuClose}>
                  <button className="accordion-trigger no-icon clearance-mobile-item">
                    <span>CLEARANCE</span>
                  </button>
                </Link>
              </div>
            )}

            {/* WHY GREEN FILTER? accordion */}
            <div className="accordion-row">
              <button
                className="accordion-trigger"
                onClick={() => toggleAccordion('why')}
              >
                <span>WHY GREEN FILTER?</span>
                <span className="accordion-icon">
                  {openAccordion === 'why' ? '×' : '+'}
                </span>
              </button>
              {openAccordion === 'why' && (
                <div className="accordion-body">
                  {WHY_LINKS.map((item) => (
                    <Link
                      key={item.label}
                      href={item.path}
                      className="sub-link"
                      onClick={handleMobileMenuClose}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* CLEANER & CARE */}
            <div className="accordion-row">
              <Link href="/cleaner-care" onClick={handleMobileMenuClose}>
                <button className="accordion-trigger no-icon">
                  <span>CLEANER &amp; CARE</span>
                </button>
              </Link>
            </div>

            {/* RESOURCES accordion */}
            <div className="accordion-row">
              <button
                className="accordion-trigger"
                onClick={() => toggleAccordion('resources')}
              >
                <span>RESOURCES</span>
                <span className="accordion-icon">
                  {openAccordion === 'resources' ? '×' : '+'}
                </span>
              </button>
              {openAccordion === 'resources' && (
                <div className="accordion-body">
                  {RESOURCES_LINKS.map((item) => (
                    <Link
                      key={item.label}
                      href={item.path}
                      className="sub-link"
                      onClick={handleMobileMenuClose}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* CONTACT US */}
            <div className="accordion-row">
              <Link href="/contact-us" onClick={handleMobileMenuClose}>
                <button className="accordion-trigger no-icon">
                  <span>CONTACT US</span>
                </button>
              </Link>
            </div>
          </DrawerContent>
        </DrawerPanel>
      </MobileDrawer>

      {/* Desktop navbar */}
      <div className="r-navbar w-nav">
        <div className="container w-clearfix">
          <div className="menu-button" onClick={() => setMobileMenuOpen(true)}>
            <FaBars />
          </div>

          <Link href="/" className="brand w-nav-brand w--current">
            <Image
              src="/assets/gf-logo-registered.webp"
              alt="Green Filter® logo"
              width={186}
              height={34}
              priority
            />
          </Link>

          <div className="nav-links">
            {/* Shop Dropdown */}
            <DropdownWrapper
              ref={shopDropdownRef}
              onMouseEnter={() => openDesktopDropdown('shop')}
              onMouseLeave={() => closeDesktopDropdown('shop')}
            >
              <DropdownButton onClick={() => openDesktopDropdown('shop')}>
                Shop{' '}
                <MdOutlineKeyboardArrowDown
                  style={{ verticalAlign: 'middle', marginLeft: 3 }}
                />
              </DropdownButton>
              <DropdownMenu $isOpen={openDropdown === 'shop'}>
                {SHOP_LINKS.map((item) =>
                  'children' in item ? (
                    <div
                      key={item.label}
                      className="dropdown-group"
                      onMouseEnter={() => openShopGroupDesktopHandler(item.id)}
                      onMouseLeave={() => closeShopGroupDesktopHandler(item.id)}
                    >
                      <button
                        type="button"
                        className="dropdown-group-trigger"
                        onClick={() => openShopGroupDesktopHandler(item.id)}
                        aria-expanded={openShopGroupDesktop === item.id}
                      >
                        {item.label}
                        <MdOutlineKeyboardArrowDown
                          className={openShopGroupDesktop === item.id ? 'rotated' : ''}
                          style={{ fontSize: 16 }}
                        />
                      </button>
                      {openShopGroupDesktop === item.id && (
                        <div className="dropdown-subgroup">
                          {item.children.map((child) => (
                            <Link
                              key={child.label}
                              href={child.path}
                              className="dropdown-item dropdown-subitem"
                            >
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      key={item.label}
                      href={item.path}
                      className="dropdown-item"
                    >
                      {item.label}
                    </Link>
                  )
                )}
              </DropdownMenu>
            </DropdownWrapper>

            {SHOW_CLEARANCE_LINK && (
              <Link href="/clearance">
                <NavButton className="clearance-nav-item">CLEARANCE</NavButton>
              </Link>
            )}

            {/* Why Green Filter Dropdown */}
            <DropdownWrapper
              ref={whyDropdownRef}
              onMouseEnter={() => openDesktopDropdown('why')}
              onMouseLeave={() => closeDesktopDropdown('why')}
            >
              <DropdownButton onClick={() => openDesktopDropdown('why')}>
                Why Green Filter?{' '}
                <MdOutlineKeyboardArrowDown
                  style={{ verticalAlign: 'middle', marginLeft: 3 }}
                />
              </DropdownButton>
              <DropdownMenu $isOpen={openDropdown === 'why'}>
                {WHY_LINKS.map((item) => (
                  <Link
                    key={item.label}
                    href={item.path}
                    className="dropdown-item"
                  >
                    {item.label}
                  </Link>
                ))}
              </DropdownMenu>
            </DropdownWrapper>

            <Link href="/cleaner-care">
              <NavButton>Cleaner &amp; Care</NavButton>
            </Link>

            {/* Resources Dropdown */}
            <DropdownWrapper
              ref={resourcesDropdownRef}
              onMouseEnter={() => openDesktopDropdown('resources')}
              onMouseLeave={() => closeDesktopDropdown('resources')}
            >
              <DropdownButton onClick={() => openDesktopDropdown('resources')}>
                Resources{' '}
                <MdOutlineKeyboardArrowDown
                  style={{ verticalAlign: 'middle', marginLeft: 3 }}
                />
              </DropdownButton>
              <DropdownMenu $isOpen={openDropdown === 'resources'}>
                {RESOURCES_LINKS.map((item) => (
                  <Link
                    key={item.label}
                    href={item.path}
                    className="dropdown-item"
                  >
                    {item.label}
                  </Link>
                ))}
              </DropdownMenu>
            </DropdownWrapper>

            <Link href="/contact-us">
              <NavButton>Contact Us</NavButton>
            </Link>
          </div>

          <div className="right-nav-div">
            <Image
              className="icon america-250-logo"
              src="/assets/america-250-logo.png"
              alt="America 250 Celebration"
              width={90}
              height={60}
              priority
            />
          </div>
        </div>
      </div>
    </HeaderWrapper>
  );
};

export default Header;

// Styled components
const HeaderWrapper = styled.div`
  position: fixed;
  left: 0px;
  top: 45px;
  right: 0px;
  z-index: 99996;

  @media (max-width: 767px) {
    top: 55px;
  }

  .r-navbar {
    display: block;
    justify-content: space-between;
    background-color: #000;
    box-shadow: 1px 1px 6px 0 rgb(0 0 0 / 7%);
  }

  .w-nav {
    position: relative;
    z-index: 9999;
  }

  .container {
    display: block;
    padding: 10px 5%;
    z-index: 1000;
  }

  .brand {
    margin-top: 8px;
    margin-right: 0px;
  }

  .w-nav-brand {
    position: relative;
    text-decoration: none;
    color: #333333;
  }

  .nav-links {
    display: inline-block;
    margin-left: 5px;
  }

  .right-nav-div {
    position: absolute;
    top: 0;
    right: 0;
    display: block;
    float: right;
    background: transparent !important;
  }

  .icon.flag,
  .icon.america-250-logo {
    width: 50px;
    margin-right: 7px;
    margin-left: 0px;
    margin-top: 10px;
    background: transparent !important;
  }

  .icon.america-250-logo {
    width: 65px;
    height: auto;
    background: transparent !important;
    display: block;
    opacity: 1;
    mix-blend-mode: normal;
  }

  .menu-button {
    display: none;
    position: absolute;
    left: 0;
    color: #fff;
    padding: 18px;
    cursor: pointer;
    z-index: 1001;
  }

  @media screen and (max-width: 912px) {
    .nav-links {
      display: none !important;
    }

    .container {
      text-align: center;
      position: relative;
      padding: 10px 5%;
    }

    .brand {
      margin-top: 0;
    }

    .icon.flag,
    .icon.america-250-logo {
      margin-top: 0;
      width: 50px;
    }

    .icon.america-250-logo {
      width: 55px;
    }

    .menu-button {
      display: inline-block;
    }

    .right-nav-div {
      right: 0 !important;
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
    }

    .brand img {
      max-height: 36px;
      width: auto;
    }
  }

  @media screen and (max-width: 479px) {
    .icon.flag {
      width: 40px;
    }

    .menu-button {
      padding: 15px;
    }
  }
`;

// Native dropdown components (replaces Ant Design Dropdown)
const DropdownWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownButton = styled.button`
  background: transparent;
  color: #fff;
  font-size: 13px;
  font-family: Lato, sans-serif;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: none;
  padding: 15px 16px;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: #00ad23;
  }
`;

const DropdownMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  background: #fff;
  min-width: 200px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  z-index: 1000;
  display: ${props => props.$isOpen ? 'block' : 'none'};
  padding: 4px 0;

  .dropdown-item {
    display: block;
    padding: 8px 16px;
    color: #333;
    text-decoration: none;
    font-size: 14px;
    font-family: Lato, sans-serif;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: #f5f5f5;
      color: #00ad23;
    }
  }

  .dropdown-group-trigger {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 16px;
    background: none;
    border: none;
    cursor: pointer;
    font-size: 14px;
    font-family: Lato, sans-serif;
    color: #333;
    text-align: left;
    transition: background-color 0.2s ease, color 0.2s ease;

    &:hover {
      background-color: #f5f5f5;
      color: #00ad23;
    }

    svg {
      transition: transform 0.2s ease;
    }

    svg.rotated {
      transform: rotate(180deg);
    }
  }

  .dropdown-subgroup {
    display: flex;
    flex-direction: column;
    background: #fafafa;
  }

  .dropdown-item.dropdown-subitem {
    padding-left: 32px;
    font-size: 13px;
  }
`;

const NavButton = styled.button`
  background: transparent;
  color: #fff;
  font-size: 13px;
  font-family: Lato, sans-serif;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: none;
  padding: 15px 16px;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: #00ad23;
  }

  &.clearance-nav-item {
    color: #ff0000;
    font-weight: 700;

    &:hover {
      color: #cc0000;
    }
  }
`;

// Native drawer components (replaces Material-UI Drawer)
const MobileDrawer = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 100001;
  pointer-events: ${props => props.$isOpen ? 'auto' : 'none'};
`;

const DrawerBackdrop = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 100000;
  opacity: ${props => props.$isOpen ? 1 : 0};
  transition: opacity 0.3s ease;
`;

const DrawerPanel = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: 85vw;
  max-width: 320px;
  background: #fff;
  z-index: 100001;
  overflow-y: auto;
  overflow-x: hidden;
  transform: translateX(${props => props.$isOpen ? '0' : '-100%'});
  transition: transform 0.3s ease;
  box-shadow: ${props => props.$isOpen ? '2px 0 8px rgba(0, 0, 0, 0.15)' : 'none'};
`;

const DrawerContent = styled.div`
  width: 100%;
  height: 100%;
  background: #fff;
  font-family: Lato, sans-serif;
  overflow-y: auto;
  position: relative;
  z-index: 1;
  pointer-events: auto;

  .drawer-header {
    display: flex;
    align-items: center;
    justify-content: center;
    background: #000;
    padding: 0 16px;
    height: 57px;
    position: relative;
    flex-shrink: 0;
  }

  .drawer-close {
    position: absolute;
    left: 16px;
    background: none;
    border: none;
    color: #fff;
    font-size: 30px;
    line-height: 1;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
  }

  .accordion-row {
    border-bottom: 1px solid #e8e8e8;
  }

  .accordion-trigger {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 20px;
    background: none;
    border: none;
    cursor: pointer;
    font: 700 13px/1 Lato, sans-serif;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: #111;
    text-align: left;
  }

  .accordion-trigger.no-icon {
    justify-content: flex-start;
  }

  .accordion-trigger:hover {
    color: #00ad23;
  }

  .accordion-trigger.clearance-mobile-item {
    color: #ff0000;
    font-weight: 700;
  }

  .accordion-trigger.clearance-mobile-item:hover {
    color: #cc0000;
  }

  .accordion-icon {
    font-size: 20px;
    font-weight: 400;
    color: #555;
    line-height: 1;
    width: 20px;
    text-align: center;
    flex-shrink: 0;
  }

  .accordion-body {
    display: flex;
    flex-direction: column;
    background: #fafafa;
  }

  .sub-link {
    display: block;
    width: 100%;
    padding: 13px 28px;
    font: 400 14px/1.4 Lato, sans-serif;
    color: #333;
    text-decoration: none;
    background: none;
    border: none;
    border-bottom: 1px solid #f0f0f0;
    cursor: pointer;
    text-align: left;
    transition: color 0.15s;
  }

  .sub-link:last-child {
    border-bottom: none;
  }

  .sub-link:hover {
    color: #00ad23;
  }

  .sub-accordion-row {
    border-top: 1px solid #f0f0f0;
  }

  .sub-accordion-trigger {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 13px 28px;
    background: none;
    border: none;
    cursor: pointer;
    font: 400 14px/1.4 Lato, sans-serif;
    color: #333;
    text-align: left;
  }

  .sub-accordion-trigger:hover {
    color: #00ad23;
  }

  .sub-accordion-body {
    display: flex;
    flex-direction: column;
    background: #f0f0f0;
  }

  .sub-link.nested {
    padding-left: 44px;
    font-size: 13px;
  }
`;
